import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import Modal from "../../../../../../components/base/Modal";
import { Badge, Button, Combobox, DateTimeRange, confirmDelete, notifyError } from "../../../../../../components/ui";
import { useAllDevices } from "../../../../../../features/devices/queries";
import { useCanSeeDevicePairing } from "../../../../../../hooks/useCanSeeDevicePairing";
import { buildAssignmentDeviceOptions } from "../utils/deviceOptions";
import { hasSecondaryDevices } from "../../../../../../features/devices/pairing";
import { useActiveTripsStore } from "../../../../../../store/admin/activeTripsStore";
import { useUsersStore } from "../../../../../../store/admin/usersStore";
import api from "../../../../../../utils/api";
import { getError } from "../../../../../../utils/getError";
import type { Trip, ThemeClasses, TimezoneCode, TripAssignment } from "../types";
import { TIMEZONE_OPTIONS } from "../types";
import { getAssignmentStatusTone } from "../utils";
import { formatAssignmentRange, formatMoment, safeImageUrl } from "../utils/assignmentFormat";

const DEFAULT_TIMEZONE: TimezoneCode = "America/Mexico_City";

const CLOSED_BY_KEYS = {
  geofence: "trips.assignments.closedByGeofence",
  driver_app: "trips.assignments.closedByDriverApp",
  trip_deleted: "trips.assignments.closedByTripDeleted",
} as const;

type AssignmentForm = {
  device_id: string;
  start_time: string;
  end_time: string;
  timezone: TimezoneCode;
};

const EMPTY_FORM: AssignmentForm = { device_id: "", start_time: "", end_time: "", timezone: DEFAULT_TIMEZONE };

const timezoneOptions = Object.entries(TIMEZONE_OPTIONS).map(([value, label]) => ({
  value: value as TimezoneCode,
  label,
}));

const hasValue = (value: unknown) => value !== null && value !== undefined && value !== "";

const hasEvidenceOf = (assignment: TripAssignment) =>
  Boolean(safeImageUrl(assignment.signature) || assignment.comment || hasValue(assignment.experience));

const cleanAssignment = (assignment: TripAssignment) => ({
  id: assignment.id,
  device_id: assignment.device_id,
  date: assignment.date,
  start_time: assignment.start_time,
  end_time: assignment.end_time,
  status: assignment.status,
  timezone: assignment.timezone,
});

interface TripAssignmentsModalProps {
  trip: Trip;
  themeClasses: ThemeClasses;
  onClose: () => void;
  t: TFunction;
}

export function TripAssignmentsModal({ trip, themeClasses, onClose, t }: TripAssignmentsModalProps) {
  const { i18n } = useTranslation();
  const locale = i18n.language || "es";
  const queryClient = useQueryClient();
  const { data: devices = [] } = useAllDevices();
  const { users, fetchUsers } = useUsersStore();
  const [form, setForm] = useState<AssignmentForm>(EMPTY_FORM);
  const [editing, setEditing] = useState<TripAssignment | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    users.length === 0 && fetchUsers({ limit: 500, page: 1 });
  }, [users.length, fetchUsers]);

  const showPairing = useCanSeeDevicePairing();

  const deviceOptions = buildAssignmentDeviceOptions(devices, {
    showPairing,
    keepDeviceId: editing?.device_id,
    primaryLabel: t("trips.assignments.primaryTag"),
    secondaryLabel: t("trips.assignments.secondaryTag"),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["trips"] });
    useActiveTripsStore.getState().fetchActiveTrips(true);
  };

  const userName = (userId?: string | null) => {
    if (!userId) return "";
    const user = users.find((u) => u.id === userId);
    return user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || userId : userId;
  };

  const unitLabel = (assignment: TripAssignment) => {
    const device = assignment.device ?? devices.find((d) => d.id === assignment.device_id);
    if (!device) return assignment.device_id;
    const label = device.name || device.identifier || assignment.device_id;
    return device.vehicle?.name ? `${label} (${device.vehicle.name})` : label;
  };

  const driverLabel = (assignment: TripAssignment) => {
    const driver = assignment.driver;
    const full = driver ? `${driver.first_name || ""} ${driver.last_name || ""}`.trim() : "";
    return full || driver?.email || userName(assignment.driver_user_id);
  };

  const closedByLabel = (assignment: TripAssignment) => {
    const key = CLOSED_BY_KEYS[assignment.closed_by as keyof typeof CLOSED_BY_KEYS];
    return [key ? t(key) : assignment.closed_by || "", userName(assignment.closed_by_user_id)]
      .filter(Boolean)
      .join(" · ");
  };

  const rangeLabel = (start?: string, end?: string) => {
    const { range, duration } = formatAssignmentRange(start, end, locale);
    return duration ? `${range} · ${duration}` : range;
  };

  const rangeLabels = {
    placeholder: t("trips.assignments.pickDates"),
    startTime: t("trips.assignments.timeStart"),
    endTime: t("trips.assignments.timeEnd"),
    done: t("trips.assignments.done"),
    previousMonth: t("trips.assignments.previousMonth"),
    nextMonth: t("trips.assignments.nextMonth"),
    invalidRange: t("trips.assignments.invalidRange"),
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
  };

  const startEdit = (assignment: TripAssignment) => {
    setEditing(assignment);
    setForm({
      device_id: assignment.device_id,
      start_time: assignment.start_time,
      end_time: assignment.end_time,
      timezone: assignment.timezone || DEFAULT_TIMEZONE,
    });
  };

  const run = async (action: () => Promise<unknown>) => {
    setSaving(true);
    try {
      await action();
      refresh();
      resetForm();
    } catch (error) {
      await notifyError(t("Error"), getError(error));
    } finally {
      setSaving(false);
    }
  };

  const submit = () => {
    const date = form.start_time.split("T")[0] || "";
    const current = trip.assignments ?? [];
    return run(() =>
      editing
        ? api.put(
            "/trips",
            {
              assignments: current.map((a) =>
                a === editing || (a.id && a.id === editing.id)
                  ? { ...cleanAssignment(a), ...form, date }
                  : cleanAssignment(a)
              ),
            },
            { params: { trip_id: trip.id } }
          )
        : api.post("/trips/assignments", { trip_id: trip.id, ...form, date, status: "pending" })
    );
  };

  const remove = async (assignment: TripAssignment) => {
    const lines = [
      t("trips.assignments.deleteStatus", { status: t(`trips.assignmentStatus.${assignment.status}`).toLowerCase() }),
      hasEvidenceOf(assignment) ? t("trips.assignments.deleteEvidence") : "",
      t("trips.assignments.deleteWarning"),
    ].filter(Boolean);
    const confirmed = await confirmDelete({
      title: t("trips.assignments.deleteTitle"),
      text: lines.join(" "),
      confirmText: t("trips.assignments.deleteConfirm"),
      cancelText: t("trips.actions.cancel"),
    });
    if (!confirmed) return;
    await run(() =>
      api.delete("/trips/assignments", {
        params: {
          trip_id: trip.id,
          device_id: assignment.device_id,
          start_time: assignment.start_time,
          date: assignment.date,
          end_time: assignment.end_time,
        },
      })
    );
  };

  const row = (label: string, value?: string | null) =>
    value ? (
      <div>
        <span className={`text-xs ${themeClasses.textSecondary}`}>{label}</span>
        <p className={`text-sm font-medium ${themeClasses.text} break-words`}>{value}</p>
      </div>
    ) : null;

  const assignments = trip.assignments ?? [];
  const canSubmit = Boolean(form.device_id && form.start_time && form.end_time && form.end_time > form.start_time) && !saving;

  return (
    <Modal
      title={`${t("trips.assignments.detailsTitle")} (${assignments.length})`}
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-full sm:w-3/5"
      content={
        <div className="space-y-3 max-h-[70vh] overflow-y-auto px-2 w-full">
          <p className={`text-sm font-semibold ${themeClasses.text}`}>{trip.title || t("trips.untitled")}</p>

          <div className={`p-3 rounded-lg border ${themeClasses.cardBorder} bg-surface space-y-2`}>
            <p className={`text-xs font-semibold uppercase ${themeClasses.textSecondary}`}>
              {editing ? t("trips.assignments.formEdit") : t("trips.assignments.formNew")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:items-end">
              <div className="sm:col-span-4">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.device")}</label>
                <Combobox
                  options={deviceOptions}
                  placeholder={t("trips.assignments.selectDevice")}
                  value={deviceOptions.find((opt) => opt.value === form.device_id) || null}
                  onChange={(option) => setForm({ ...form, device_id: option?.value || "" })}
                  isSearchable
                  isClearable
                  isDisabled={Boolean(editing && editing.status !== "pending")}
                />
                {!showPairing && form.device_id && hasSecondaryDevices(devices, form.device_id) && (
                  <p className="mt-1 text-xs text-content-muted">{t("trips.assignments.hasSecondaries")}</p>
                )}
              </div>
              <div className="sm:col-span-4">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.schedule")}</label>
                <DateTimeRange
                  locale={locale}
                  labels={rangeLabels}
                  value={{ start: form.start_time, end: form.end_time }}
                  onChange={({ start, end }) => setForm((current) => ({ ...current, start_time: start, end_time: end }))}
                />
              </div>
              <div className="sm:col-span-4">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.timezone")}</label>
                <Combobox
                  options={timezoneOptions}
                  placeholder={t("trips.assignments.selectTimezone")}
                  value={timezoneOptions.find((opt) => opt.value === form.timezone) || null}
                  onChange={(option) => setForm({ ...form, timezone: option?.value || DEFAULT_TIMEZONE })}
                  isSearchable
                />
              </div>
              <div className="sm:col-span-12 flex gap-2 sm:justify-end">
                <Button type="button" size="sm" className="flex-1 sm:flex-none sm:px-6" loading={saving} disabled={!canSubmit} onClick={submit}>
                  {editing ? t("trips.assignments.save") : t("trips.assignments.add")}
                </Button>
                {editing && (
                  <Button type="button" size="sm" variant="outline" className="flex-1 sm:flex-none" disabled={saving} onClick={resetForm}>
                    {t("trips.actions.cancel")}
                  </Button>
                )}
              </div>
            </div>
            {form.start_time && form.end_time && (
              <p className={`text-xs ${themeClasses.textSecondary}`}>
                <i className="ri-calendar-event-line mr-1" />
                {rangeLabel(form.start_time, form.end_time)}
              </p>
            )}
          </div>

          {assignments.length === 0 && (
            <p className={`text-sm ${themeClasses.textSecondary} italic`}>{t("trips.assignments.empty")}</p>
          )}
          {assignments.map((assignment, index) => {
            const zone = assignment.timezone;
            const signature = safeImageUrl(assignment.signature);
            const hasEvidence = hasEvidenceOf(assignment);
            return (
              <div
                key={assignment.id ?? index}
                className={`space-y-3 p-3 rounded-lg border ${themeClasses.cardBorder} bg-surface`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm font-semibold ${themeClasses.text} truncate`}>{unitLabel(assignment)}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge tone={getAssignmentStatusTone(assignment.status)}>
                      {t(`trips.assignmentStatus.${assignment.status}`)}
                    </Badge>
                    {assignment.status === "pending" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        iconOnly
                        icon={<i className="ri-edit-line" />}
                        title={t("trips.actions.edit")}
                        disabled={saving}
                        onClick={() => startEdit(assignment)}
                      />
                    )}
                    <Button
                      type="button"
                      variant="dangerSoft"
                      size="sm"
                      iconOnly
                      icon={<i className="ri-delete-bin-line" />}
                      title={t("trips.actions.delete")}
                      disabled={saving}
                      onClick={() => remove(assignment)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {row(t("trips.assignments.driver"), driverLabel(assignment))}
                  {row(t("trips.assignments.timezone"), zone ? TIMEZONE_OPTIONS[zone] || zone : "")}
                  <div className="sm:col-span-2">
                    {row(t("trips.assignments.window"), rangeLabel(assignment.start_time, assignment.end_time))}
                  </div>
                  {row(t("trips.assignments.completedAt"), formatMoment(assignment.completed_at, locale, zone))}
                  {row(t("trips.assignments.cancelledAt"), formatMoment(assignment.cancelled_at, locale, zone))}
                  {row(t("trips.assignments.closedBy"), closedByLabel(assignment))}
                </div>

                <div className={`border-t ${themeClasses.cardBorder} pt-3 space-y-3`}>
                  <p className={`text-xs font-semibold uppercase ${themeClasses.textSecondary}`}>
                    {t("trips.assignments.evidence")}
                  </p>
                  {!hasEvidence && (
                    <p className={`text-sm ${themeClasses.textSecondary} italic`}>{t("trips.assignments.noEvidence")}</p>
                  )}
                  {signature && (
                    <div>
                      <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.assignments.signature")}</span>
                      <a
                        href={signature}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t("trips.assignments.viewSignature")}
                        className={`mt-1 block w-fit rounded-lg border ${themeClasses.cardBorder} bg-white p-2`}
                      >
                        <img src={signature} alt={t("trips.assignments.signature")} className="max-h-32 max-w-full object-contain" />
                      </a>
                    </div>
                  )}
                  {hasValue(assignment.experience) && (
                    <div>
                      <span className={`text-xs ${themeClasses.textSecondary}`}>{t("trips.assignments.experience")}</span>
                      <p className={`text-sm font-medium ${themeClasses.text}`}>
                        <i className="ri-star-fill text-warning mr-1" />
                        {String(assignment.experience)}
                      </p>
                    </div>
                  )}
                  {row(t("trips.assignments.comment"), assignment.comment)}
                  {hasEvidence &&
                    row(
                      t("trips.assignments.evidenceBy"),
                      [userName(assignment.evidence_by_user_id), formatMoment(assignment.evidence_at, locale, zone)]
                        .filter(Boolean)
                        .join(" · ")
                    )}
                </div>
              </div>
            );
          })}
        </div>
      }
    />
  );
}
